import { supabase } from '../lib/supabase'
import { 
  WikiArticle, 
  WikiCategory, 
  WikiDepartment,
  WikiTag, 
  WikiArticleVersion,
  WikiFavorite,
  WikiView,
  WikiStats,
  ArticleStats,
  CreateArticlePayload, 
  UpdateArticlePayload,
  CreateCategoryPayload,
  CreateDepartmentPayload,
  CreateTagPayload,
  ArticleFilters
} from '../types/wiki'

// Função auxiliar para gerar slug
function generateSlug(text: string): string {
  if (!text || text.trim() === '') {
    // Se o título estiver vazio, gerar um slug baseado em timestamp
    return `artigo-${Date.now()}`
  }
  
  const slug = text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^a-z0-9\s-]/g, '') // Remove caracteres especiais
    .replace(/\s+/g, '-') // Substitui espaços por hífens
    .replace(/-+/g, '-') // Remove hífens duplicados
    .trim()
  
  // Se o slug resultante estiver vazio, usar timestamp
  if (!slug || slug === '') {
    return `artigo-${Date.now()}`
  }
  
  return slug
}

// Gera um slug único para a tabela de artigos, evitando colisões com o índice único
async function generateUniqueArticleSlug(title: string, excludeId?: string, forceUnique: boolean = false): Promise<string> {
  const baseSlug = generateSlug(title)

  // Se forçado a ser único, sempre adicionar timestamp e random para garantir unicidade absoluta
  if (forceUnique) {
    const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
    return `${baseSlug}-${uniqueSuffix}`
  }

  // Se o slug base já contém timestamp (artigo sem título), retornar diretamente
  if (baseSlug.startsWith('artigo-') && /^\d+$/.test(baseSlug.split('-')[1])) {
    return baseSlug
  }

  try {
    // Buscar todos os slugs que começam com o baseSlug
    const { data: existing, error } = await supabase
      .from('wiki_articles')
      .select('id, slug')
      .ilike('slug', `${baseSlug}%`)

    if (error) {
      // Em caso de erro na leitura, adicionar timestamp e random para garantir unicidade
      console.warn('Erro ao verificar slugs existentes:', error)
      const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
      return `${baseSlug}-${uniqueSuffix}`
    }

    const existingSlugs = new Set(
      (existing || [])
        .filter((row: any) => !excludeId || row.id !== excludeId)
        .map((row: any) => row.slug)
    )

    if (!existingSlugs.has(baseSlug)) {
      return baseSlug
    }

    // Encontrar o próximo sufixo disponível: base-2, base-3, ...
    let suffix = 2
    while (existingSlugs.has(`${baseSlug}-${suffix}`)) {
      suffix += 1
      // Limite de segurança para evitar loop infinito
      if (suffix > 10000) {
        const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
        return `${baseSlug}-${uniqueSuffix}`
      }
    }
    return `${baseSlug}-${suffix}`
  } catch (err) {
    // Em caso de qualquer erro, adicionar timestamp e random para garantir unicidade
    console.error('Erro ao gerar slug único:', err)
    const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
    return `${baseSlug}-${uniqueSuffix}`
  }
}

// ===== CATEGORIAS =====
/**
 * Lista todas as categorias da Wiki ordenadas alfabeticamente
 * @returns Promise<WikiCategory[]> Array de categorias
 */
export async function listCategories(): Promise<WikiCategory[]> {
  const { data, error } = await supabase
    .from('wiki_categories')
    .select('*')
    .order('name', { ascending: true })

  if (error) {
    throw new Error(`Erro ao carregar categorias: ${error.message}`)
  }

  return data || []
}

/**
 * Lista todos os departamentos da Wiki ordenados alfabeticamente
 * @returns Promise<WikiDepartment[]> Array de departamentos
 */
export async function listDepartments(): Promise<WikiDepartment[]> {
  const { data, error } = await supabase
    .from('wiki_departments')
    .select('*')
    .order('name', { ascending: true })

  if (error) {
    throw new Error(`Erro ao carregar departamentos: ${error.message}`)
  }

  return data || []
}

export async function createDepartment(payload: CreateDepartmentPayload): Promise<string> {
  const slug = generateSlug(payload.name)
  
  const { data, error } = await supabase
    .from('wiki_departments')
    .insert({
      name: payload.name,
      slug,
      description: payload.description,
      color: payload.color
    })
    .select('id')
    .single()

  if (error) {
    throw new Error(`Erro ao criar departamento: ${error.message}`)
  }

  return data.id
}

export async function updateDepartment(id: string, payload: CreateDepartmentPayload): Promise<void> {
  const slug = generateSlug(payload.name)
  
  const { error } = await supabase
    .from('wiki_departments')
    .update({
      name: payload.name,
      slug,
      description: payload.description,
      color: payload.color
    })
    .eq('id', id)

  if (error) {
    throw new Error(`Erro ao atualizar departamento: ${error.message}`)
  }
}

export async function deleteDepartment(id: string): Promise<void> {
  const { error } = await supabase
    .from('wiki_departments')
    .delete()
    .eq('id', id)

  if (error) {
    throw new Error(`Erro ao excluir departamento: ${error.message}`)
  }
}

export async function createCategory(payload: CreateCategoryPayload): Promise<string> {
  const slug = generateSlug(payload.name)
  
  const { data, error } = await supabase
    .from('wiki_categories')
    .insert({
      name: payload.name,
      slug,
      description: payload.description
    })
    .select('id')
    .single()

  if (error) {
    throw new Error(`Erro ao criar categoria: ${error.message}`)
  }

  return data.id
}

export async function updateCategory(id: string, payload: CreateCategoryPayload): Promise<void> {
  const slug = generateSlug(payload.name)
  
  const { error } = await supabase
    .from('wiki_categories')
    .update({
      name: payload.name,
      slug,
      description: payload.description
    })
    .eq('id', id)

  if (error) {
    throw new Error(`Erro ao atualizar categoria: ${error.message}`)
  }
}

export async function deleteCategory(id: string): Promise<void> {
  const { error } = await supabase
    .from('wiki_categories')
    .delete()
    .eq('id', id)

  if (error) {
    throw new Error(`Erro ao excluir categoria: ${error.message}`)
  }
}

// ===== TAGS =====
export async function listTags(): Promise<WikiTag[]> {
  const { data, error } = await supabase
    .from('wiki_tags')
    .select('*')
    .order('name', { ascending: true })

  if (error) {
    throw new Error(`Erro ao carregar tags: ${error.message}`)
  }

  return data || []
}

export async function createTag(payload: CreateTagPayload): Promise<string> {
  const slug = generateSlug(payload.name)
  
  const { data, error } = await supabase
    .from('wiki_tags')
    .insert({
      name: payload.name,
      slug
    })
    .select('id')
    .single()

  if (error) {
    throw new Error(`Erro ao criar tag: ${error.message}`)
  }

  return data.id
}

export async function updateTag(id: string, payload: CreateTagPayload): Promise<void> {
  const slug = generateSlug(payload.name)
  
  const { error } = await supabase
    .from('wiki_tags')
    .update({
      name: payload.name,
      slug
    })
    .eq('id', id)

  if (error) {
    throw new Error(`Erro ao atualizar tag: ${error.message}`)
  }
}

export async function deleteTag(id: string): Promise<void> {
  const { error } = await supabase
    .from('wiki_tags')
    .delete()
    .eq('id', id)

  if (error) {
    throw new Error(`Erro ao excluir tag: ${error.message}`)
  }
}

// ===== USUÁRIOS =====
export async function getUserInfo(userId: string): Promise<{ name: string; email: string } | null> {
  try {
    const { data, error } = await supabase.rpc('get_user_info', {
      user_id: userId
    })
    
    if (error || !data) {
      return null
    }

    return {
      name: data.name || 'Usuário',
      email: data.email || ''
    }
  } catch (error) {
    console.error('Erro ao buscar informações do usuário:', error)
    return null
  }
}

export async function getMultipleUsersInfo(userIds: string[]): Promise<Record<string, { name: string; email: string }>> {
  try {
    const { data, error } = await supabase.rpc('get_multiple_users_info', {
      user_ids: userIds
    })
    
    if (error) {
      console.error('Erro na função RPC:', error)
      return {}
    }

    if (!data) {
      return {}
    }

    const userMap: Record<string, { name: string; email: string }> = {}
    if (Array.isArray(data)) {
      data.forEach((user: any) => {
        userMap[user.id] = {
          name: user.name || 'Usuário',
          email: user.email || ''
        }
      })
    }
    
    return userMap
  } catch (error) {
    console.error('Erro ao buscar informações dos usuários:', error)
    return {}
  }
}

export async function getAllUsers(): Promise<Array<{id: string, name: string}>> {
  try {
    const { data: allProfilesData, error: allProfilesError } = await supabase
      .from('profiles')
      .select('id, name, is_active')
      .order('name', { ascending: true })
    
    if (allProfilesError) {
      throw new Error(`Erro na consulta: ${allProfilesError.message}`)
    }
    
    if (!allProfilesData || allProfilesData.length === 0) {
      return []
    }
    
    // Filtrar usuários ativos, ou retornar todos se não houver ativos
    const activeUsers = allProfilesData.filter(user => user.is_active === true)
    const usersToReturn = activeUsers.length > 0 ? activeUsers : allProfilesData
    
    const users = usersToReturn.map((user: any) => ({
      id: user.id,
      name: user.name || 'Usuário'
    }))
    
    return users
    
  } catch (error) {
    return []
  }
}


// ===== ARTIGOS =====
/**
 * Atualiza artigos que não possuem created_by definido
 * Utilizado para corrigir artigos criados antes da implementação do versionamento
 * @returns Promise<void>
 */
export async function updateArticlesWithoutUser(): Promise<void> {
  try {
    // Obter o usuário atual
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      return
    }

    // Atualizar artigos que não têm created_by
    const { error } = await supabase
      .from('wiki_articles')
      .update({ created_by: user.id })
      .is('created_by', null)

    if (error) {
      console.error('Erro ao atualizar artigos:', error)
    }
  } catch (error) {
    console.error('Erro ao atualizar artigos:', error)
  }
}

// ===== VERSIONAMENTO =====
export async function createArticleVersion(
  articleId: string, 
  versionNumber: number, 
  title: string, 
  body: string, 
  changeSummary?: string
): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      throw new Error('Usuário não autenticado')
    }


    const { error } = await supabase
      .from('wiki_article_versions')
      .insert({
        article_id: articleId,
        version_number: versionNumber,
        title,
        body,
        created_by: user.id,
        change_summary: changeSummary
      })

    if (error) {
      console.error('Erro ao inserir versão:', error)
      throw new Error(`Erro ao criar versão: ${error.message}`)
    }

  } catch (error) {
    console.error('Erro ao criar versão do artigo:', error)
    throw error
  }
}

export async function getArticleVersions(articleId: string): Promise<WikiArticleVersion[]> {
  try {
    const { data, error } = await supabase
      .from('wiki_article_versions')
      .select(`
        *
      `)
      .eq('article_id', articleId)
      .order('version_number', { ascending: false })

    if (error) {
      throw new Error(`Erro ao buscar versões: ${error.message}`)
    }

    const versions = (data || [])

    // Resolver nomes dos autores das versões
    const authorIds = Array.from(new Set(
      versions.map((v: any) => v.created_by).filter(Boolean)
    )) as string[]

    let userMap: Record<string, { name: string; email: string }> = {}
    if (authorIds.length > 0) {
      try {
        userMap = await getMultipleUsersInfo(authorIds)
      } catch (e) {
        console.warn('Falha ao resolver nomes de autores das versões:', e)
      }
    }

    const versionsWithNames = versions.map((v: any) => ({
      ...v,
      user_name: v.created_by ? (userMap[v.created_by]?.name || 'Usuário') : 'Usuário'
    }))

    return versionsWithNames
  } catch (error) {
    console.error('Erro ao buscar versões do artigo:', error)
    return []
  }
}

export async function restoreArticleVersion(articleId: string, versionNumber: number): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      throw new Error('Usuário não autenticado')
    }

    // Buscar a versão específica
    const { data: version, error: versionError } = await supabase
      .from('wiki_article_versions')
      .select('*')
      .eq('article_id', articleId)
      .eq('version_number', versionNumber)
      .single()

    if (versionError || !version) {
      throw new Error('Versão não encontrada')
    }

    // Atualizar o artigo atual com os dados da versão
    const { error: updateError } = await supabase
      .from('wiki_articles')
      .update({
        title: version.title,
        body: version.body,
        updated_at: new Date().toISOString()
      })
      .eq('id', articleId)

    if (updateError) {
      throw new Error(`Erro ao restaurar versão: ${updateError.message}`)
    }

    // Criar uma nova versão com os dados restaurados
    const { data: currentArticle } = await supabase
      .from('wiki_articles')
      .select('current_version')
      .eq('id', articleId)
      .single()

    const newVersionNumber = (currentArticle?.current_version || 0) + 1

    await createArticleVersion(
      articleId,
      newVersionNumber,
      version.title,
      version.body,
      `Restaurado da versão ${versionNumber}`
    )

    // Atualizar current_version
    await supabase
      .from('wiki_articles')
      .update({ current_version: newVersionNumber })
      .eq('id', articleId)

  } catch (error) {
    console.error('Erro ao restaurar versão:', error)
    throw error
  }
}

// ===== FAVORITOS =====
/**
 * Adicionar artigo aos favoritos do usuário
 * @param articleId ID do artigo a ser favoritado
 * @returns Promise<void>
 */
export async function addFavorite(articleId: string): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      throw new Error('Usuário não autenticado')
    }

    const { error } = await supabase
      .from('wiki_favorites')
      .insert({
        user_id: user.id,
        article_id: articleId
      })

    if (error) {
      throw new Error(`Erro ao adicionar favorito: ${error.message}`)
    }
  } catch (error) {
    console.error('Erro ao adicionar favorito:', error)
    throw error
  }
}

/**
 * Remover artigo dos favoritos do usuário
 * @param articleId ID do artigo a ser removido dos favoritos
 * @returns Promise<void>
 */
export async function removeFavorite(articleId: string): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      throw new Error('Usuário não autenticado')
    }

    const { error } = await supabase
      .from('wiki_favorites')
      .delete()
      .eq('user_id', user.id)
      .eq('article_id', articleId)

    if (error) {
      throw new Error(`Erro ao remover favorito: ${error.message}`)
    }
  } catch (error) {
    console.error('Erro ao remover favorito:', error)
    throw error
  }
}

/**
 * Listar favoritos do usuário atual
 * @returns Promise<WikiFavorite[]> Array de favoritos com dados do artigo
 */
export async function listFavorites(): Promise<WikiFavorite[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      throw new Error('Usuário não autenticado')
    }

    const { data, error } = await supabase
      .from('wiki_favorites')
      .select(`
        *,
        article:wiki_articles(
          *,
          category:wiki_categories(*),
          tags:wiki_article_tags(
            tag:wiki_tags(*)
          )
        )
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })

    if (error) {
      throw new Error(`Erro ao buscar favoritos: ${error.message}`)
    }

    return data || []
  } catch (error) {
    console.error('Erro ao buscar favoritos:', error)
    return []
  }
}

/**
 * Verificar se um artigo está nos favoritos do usuário
 * @param articleId ID do artigo a ser verificado
 * @returns Promise<boolean> True se estiver nos favoritos
 */
export async function isFavorite(articleId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      return false
    }

    const { data, error } = await supabase
      .from('wiki_favorites')
      .select('id')
      .eq('user_id', user.id)
      .eq('article_id', articleId)
      .single()

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      throw new Error(`Erro ao verificar favorito: ${error.message}`)
    }

    return !!data
  } catch (error) {
    console.error('Erro ao verificar favorito:', error)
    return false
  }
}

/**
 * Buscar IDs dos artigos favoritados pelo usuário
 * @returns Promise<string[]> Array de IDs dos artigos favoritados
 */
export async function getFavoriteArticleIds(): Promise<string[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      return []
    }

    const { data, error } = await supabase
      .from('wiki_favorites')
      .select('article_id')
      .eq('user_id', user.id)

    if (error) {
      throw new Error(`Erro ao buscar IDs dos favoritos: ${error.message}`)
    }

    return (data || []).map(fav => fav.article_id)
  } catch (error) {
    console.error('Erro ao buscar IDs dos favoritos:', error)
    return []
  }
}

// ===== ESTATÍSTICAS E ANALYTICS =====
/**
 * Registrar visualização de um artigo
 * @param articleId ID do artigo visualizado
 * @returns Promise<void>
 */
export async function trackArticleView(articleId: string): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    // Obter informações do navegador
    const userAgent = navigator.userAgent
    
    const { error } = await supabase.rpc('track_article_view', {
      article_id_param: articleId,
      user_id_param: user?.id || null,
      user_agent_param: userAgent
    })

    if (error) {
      console.warn('Erro ao registrar visualização:', error)
      // Não falha a operação se não conseguir registrar a visualização
    }
  } catch (error) {
    console.warn('Erro ao registrar visualização:', error)
    // Não falha a operação se não conseguir registrar a visualização
  }
}

/**
 * Obter estatísticas gerais da Wiki
 * @returns Promise<WikiStats> Estatísticas gerais
 */
export async function getWikiStats(): Promise<WikiStats> {
  try {
    const { data, error } = await supabase.rpc('get_wiki_stats')

    if (error) {
      throw new Error(`Erro ao buscar estatísticas: ${error.message}`)
    }

    return data || {
      total_articles: 0,
      total_categories: 0,
      total_tags: 0,
      total_departments: 0,
      articles_by_status: {},
      articles_by_department: {},
      articles_by_category: {},
      total_views: 0,
      unique_viewers: 0,
      most_viewed_articles: [],
      recent_views: [],
      views_by_day: {}
    }
  } catch (error) {
    console.error('Erro ao buscar estatísticas:', error)
    return {
      total_articles: 0,
      total_categories: 0,
      total_tags: 0,
      total_departments: 0,
      articles_by_status: {},
      articles_by_department: {},
      articles_by_category: {},
      total_views: 0,
      unique_viewers: 0,
      most_viewed_articles: [],
      recent_views: [],
      views_by_day: {}
    }
  }
}

export async function getWikiManagementStats(): Promise<WikiStats> {
  // Por enquanto, usar a mesma função de estatísticas gerais
  // Pode ser expandida no futuro para incluir estatísticas específicas de gerenciamento
  return getWikiStats()
}

/**
 * Obter estatísticas apenas de artigos publicados
 * @returns Promise<{total_articles: number, total_categories: number, total_tags: number}>
 */
export async function getPublishedWikiStats(): Promise<{
  total_articles: number
  total_categories: number
  total_tags: number
}> {
  try {
    // Contar artigos publicados
    const { count: totalArticles, error: articlesError } = await supabase
      .from('wiki_articles')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'published')

    if (articlesError) throw articlesError

    // Contar categorias únicas que têm artigos publicados
    const { data: categoriesData, error: categoriesError } = await supabase
      .from('wiki_articles')
      .select('category_id')
      .eq('status', 'published')
      .not('category_id', 'is', null)

    if (categoriesError) throw categoriesError

    const uniqueCategories = new Set(
      (categoriesData || [])
        .map((a: any) => a.category_id)
        .filter(Boolean)
    )

    // Contar tags únicas que têm artigos publicados
    // Buscar todos os artigos publicados com suas tags
    const { data: articlesWithTags, error: tagsError } = await supabase
      .from('wiki_articles')
      .select(`
        id,
        tags:wiki_article_tags(
          tag_id
        )
      `)
      .eq('status', 'published')

    if (tagsError) throw tagsError

    const uniqueTags = new Set<string>()
    ;(articlesWithTags || []).forEach((article: any) => {
      if (article.tags && Array.isArray(article.tags)) {
        article.tags.forEach((tagRel: any) => {
          if (tagRel.tag_id) {
            uniqueTags.add(tagRel.tag_id)
          }
        })
      }
    })

    return {
      total_articles: totalArticles || 0,
      total_categories: uniqueCategories.size,
      total_tags: uniqueTags.size
    }
  } catch (error) {
    console.error('Erro ao buscar estatísticas de artigos publicados:', error)
    return {
      total_articles: 0,
      total_categories: 0,
      total_tags: 0
    }
  }
}

/**
 * Obter artigos mais visualizados
 * @param limit Limite de resultados (padrão: 10)
 * @returns Promise<any[]> Lista de artigos mais visualizados
 */
export async function getMostViewedArticles(limit: number = 10): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .from('most_viewed_articles')
      .select('*')
      .order('view_count', { ascending: false })
      .limit(limit)

    if (error) {
      throw new Error(`Erro ao buscar artigos mais visualizados: ${error.message}`)
    }

    return data || []
  } catch (error) {
    console.error('Erro ao buscar artigos mais visualizados:', error)
    return []
  }
}

/**
 * Obter estatísticas de um artigo específico
 * @param articleId ID do artigo
 * @returns Promise<ArticleStats> Estatísticas do artigo
 */
export async function getArticleStats(articleId: string): Promise<ArticleStats> {
  try {
    const { data, error } = await supabase.rpc('get_article_stats', {
      article_id_param: articleId
    })

    if (error) {
      throw new Error(`Erro ao buscar estatísticas do artigo: ${error.message}`)
    }

    return data || {
      article_id: articleId,
      total_views: 0,
      unique_viewers: 0,
      views_by_day: {},
      recent_viewers: []
    }
  } catch (error) {
    console.error('Erro ao buscar estatísticas do artigo:', error)
    return {
      article_id: articleId,
      total_views: 0,
      unique_viewers: 0,
      views_by_day: {},
      recent_viewers: []
    }
  }
}

/**
 * Listar visualizações de um artigo
 * @param articleId ID do artigo
 * @param limit Limite de resultados
 * @returns Promise<WikiView[]> Lista de visualizações
 */
export async function getArticleViews(articleId: string, limit: number = 50): Promise<WikiView[]> {
  try {
    const { data, error } = await supabase
      .from('wiki_views')
      .select('*')
      .eq('article_id', articleId)
      .order('viewed_at', { ascending: false })
      .limit(limit)

    if (error) {
      throw new Error(`Erro ao buscar visualizações: ${error.message}`)
    }

    return data || []
  } catch (error) {
    console.error('Erro ao buscar visualizações:', error)
    return []
  }
}

// ===== DUPLICAR ARTIGO =====
/**
 * Duplicar um artigo existente
 * @param articleId ID do artigo a ser duplicado
 * @returns Promise<WikiArticle> Artigo duplicado
 */
export async function duplicateArticle(articleId: string): Promise<WikiArticle> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      throw new Error('Usuário não autenticado')
    }

    // Buscar o artigo original
    const { data: originalArticle, error: fetchError } = await supabase
      .from('wiki_articles')
      .select(`
        *,
        tags:wiki_article_tags(
          tag:wiki_tags(*)
        )
      `)
      .eq('id', articleId)
      .single()

    if (fetchError) {
      throw new Error(`Erro ao buscar artigo: ${fetchError.message}`)
    }

    if (!originalArticle) {
      throw new Error('Artigo não encontrado')
    }

    // Criar o artigo duplicado
    const duplicatedTitle = `[CÓPIA] ${originalArticle.title}`
    const duplicatedArticle = {
      title: duplicatedTitle,
      slug: await generateUniqueArticleSlug(duplicatedTitle), // Garantir slug único para a cópia
      body: originalArticle.body,
      category_id: originalArticle.category_id,
      department_id: originalArticle.department_id,
      status: 'draft' as const, // Sempre começar como rascunho
      created_by: user.id
    }

    const { data: newArticle, error: createError } = await supabase
      .from('wiki_articles')
      .insert(duplicatedArticle)
      .select()
      .single()

    if (createError) {
      throw new Error(`Erro ao criar artigo duplicado: ${createError.message}`)
    }

    // Copiar tags se existirem
    if (originalArticle.tags && originalArticle.tags.length > 0) {
      const tagInserts = originalArticle.tags.map((tagRelation: any) => ({
        article_id: newArticle.id,
        tag_id: tagRelation.tag.id
      }))

      const { error: tagsError } = await supabase
        .from('wiki_article_tags')
        .insert(tagInserts)

      if (tagsError) {
        console.warn('Erro ao copiar tags:', tagsError)
        // Não falha a operação se não conseguir copiar as tags
      }
    }

    // Criar versão inicial para o artigo duplicado
    try {
      await createArticleVersion(
        newArticle.id,
        1,
        newArticle.title,
        newArticle.body,
        'Versão inicial (artigo duplicado)'
      )

      // Atualizar current_version
      await supabase
        .from('wiki_articles')
        .update({ current_version: 1 })
        .eq('id', newArticle.id)
    } catch (versionError) {
      console.warn('Erro ao criar versão inicial (não crítico):', versionError)
      // Não falha a operação se não conseguir criar a versão
    }
    return newArticle
  } catch (error) {
    console.error('Erro ao duplicar artigo:', error)
    throw error
  }
}
export async function listArticles(filters: ArticleFilters = {}, page: number = 1, limit: number = 10): Promise<{ articles: WikiArticle[], total: number, page: number, limit: number, totalPages: number }> {
  let query = supabase
    .from('wiki_articles')
    .select(`
      *,
      category:wiki_categories(*),
      tags:wiki_article_tags(
        tag:wiki_tags(*)
      )
    `)
    .order(filters.sort_by || 'created_at', { ascending: filters.sort_order === 'asc' })

  // Aplicar filtros
  if (filters.query) {
    query = query.or(`title.ilike.%${filters.query}%,body.ilike.%${filters.query}%`)
  }

  if (filters.category_id) {
    query = query.eq('category_id', filters.category_id)
  }

  if (filters.department_id) {
    query = query.eq('department_id', filters.department_id)
  }

  if (filters.status) {
    query = query.eq('status', filters.status)
  }

  if (filters.created_by) {
    query = query.eq('created_by', filters.created_by)
  }

  if (filters.responsible_id) {
    query = query.eq('responsible_id', filters.responsible_id)
  }

  // Filtros de data
  if (filters.date_from) {
    query = query.gte('created_at', filters.date_from)
  }

  if (filters.date_to) {
    query = query.lte('created_at', filters.date_to)
  }

  // Calcular offset para paginação
  const offset = (page - 1) * limit

  // Buscar total de registros (sem paginação)
  const { count: totalCount } = await supabase
    .from('wiki_articles')
    .select('*', { count: 'exact', head: true })

  // Buscar artigos com paginação
  const { data, error } = await query.range(offset, offset + limit - 1)

  if (error) {
    throw new Error(`Erro ao carregar artigos: ${error.message}`)
  }

  // Processar dados para incluir tags corretamente
  let articles = (data || []).map(article => ({
    ...article,
    tags: article.tags?.map((t: any) => t.tag).filter(Boolean) || []
  }))

  // Anexar departamentos via join no cliente
  const departmentIds = Array.from(new Set(
    (articles.map(a => a.department_id).filter(Boolean) as string[])
  ))

  if (departmentIds.length > 0) {
    const { data: departmentsData, error: deptError } = await supabase
      .from('wiki_departments')
      .select('*')
      .in('id', departmentIds)

    if (!deptError && departmentsData) {
      const deptMap = new Map<string, any>(
        departmentsData.map((d: any) => [d.id, d])
      )
      articles = articles.map(a => ({
        ...a,
        department: a.department_id ? deptMap.get(a.department_id) : undefined
      }))
    }
  }

  const total = totalCount || 0
  const totalPages = Math.ceil(total / limit)

  return {
    articles,
    total,
    page,
    limit,
    totalPages
  }
}

export async function getArticle(id: string): Promise<WikiArticle | null> {
  const { data, error } = await supabase
    .from('wiki_articles')
    .select(`
      *,
      category:wiki_categories(*),
      tags:wiki_article_tags(
        tag:wiki_tags(*)
      )
    `)
    .eq('id', id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') {
      return null // Artigo não encontrado
    }
    throw new Error(`Erro ao carregar artigo: ${error.message}`)
  }

  // Anexar departamento (join no cliente)
  let department: any | undefined
  if (data.department_id) {
    const { data: dept } = await supabase
      .from('wiki_departments')
      .select('*')
      .eq('id', data.department_id)
      .single()
    department = dept || undefined
  }

  return {
    ...data,
    tags: data.tags?.map((t: any) => t.tag).filter(Boolean) || [],
    department
  }
}

export async function createArticle(payload: CreateArticlePayload): Promise<string> {
  // Obter o usuário atual
  const { data: { user } } = await supabase.auth.getUser()
  
  // Tentar criar o artigo com retry em caso de conflito de slug
  let attempts = 0
  const maxAttempts = 5
  let article: any = null
  let articleError: any = null
  
  while (attempts < maxAttempts) {
    // Na primeira tentativa, tentar slug normal. Nas seguintes, forçar único
    const forceUnique = attempts > 0
    const slug = await generateUniqueArticleSlug(payload.title, undefined, forceUnique)
    
    const { data, error } = await supabase
      .from('wiki_articles')
      .insert({
        title: payload.title,
        slug,
        body: payload.body,
        category_id: payload.category_id,
        department_id: payload.department_id,
        responsible_id: payload.responsible_id,
        status: payload.status,
        created_by: user?.id
      })
      .select('id')
      .single()

    if (error) {
      
      // Verificar se é erro de chave duplicada (slug) - verificar múltiplos formatos
      const errorMessage = (error.message || '').toLowerCase()
      const errorCode = error.code || ''
      const errorDetails = (error.details || '').toLowerCase()
      
      const isDuplicateSlug = (
        errorCode === '23505' || 
        errorMessage.includes('duplicate key') ||
        errorMessage.includes('unique constraint') ||
        errorMessage.includes('slug') ||
        errorDetails.includes('slug') ||
        errorMessage.includes('violates unique constraint')
      )
      
      // Se for erro de slug duplicado e ainda temos tentativas, tentar novamente
      if (isDuplicateSlug && attempts < maxAttempts - 1) {
        attempts++
        // Aguardar um pouco antes de tentar novamente (evitar race condition)
        await new Promise(resolve => setTimeout(resolve, 200 * attempts))
        continue
      }
      
      // Se não for erro de slug duplicado ou se esgotaram as tentativas, lançar erro
      // Mas se ainda temos tentativas e não identificamos o tipo de erro, tentar com slug único
      if (!isDuplicateSlug && attempts < maxAttempts - 1 && errorMessage.includes('constraint')) {
        attempts++
        await new Promise(resolve => setTimeout(resolve, 200 * attempts))
        continue
      }
      
      articleError = error
      break
    }
    
    article = data
    break
  }

  if (articleError || !article) {
    throw new Error(`Erro ao criar artigo: ${articleError?.message || 'Erro desconhecido'}`)
  }

  // Criar versão inicial
  await createArticleVersion(
    article.id,
    1,
    payload.title,
    payload.body,
    'Versão inicial'
  )

  // Atualizar current_version
  const { error: updateError } = await supabase
    .from('wiki_articles')
    .update({ current_version: 1 })
    .eq('id', article.id)

  if (updateError) {
    // Não lançar erro aqui para não impedir a criação do artigo
  }

  // Processar tags
  if (payload.tags && payload.tags.length > 0) {
    try {
      await upsertArticleTags(article.id, payload.tags)
    } catch (tagError) {
      // Erro não crítico ao processar tags
      // Não lançar erro aqui para não impedir a criação do artigo
    }
  }

  return article.id
}

export async function updateArticle(payload: UpdateArticlePayload): Promise<void> {
  const slug = await generateUniqueArticleSlug(payload.title, payload.id)
  
  // Atualizar artigo
  const { error: articleError } = await supabase
    .from('wiki_articles')
    .update({
      title: payload.title,
      slug,
      body: payload.body,
      category_id: payload.category_id,
      department_id: payload.department_id,
      responsible_id: payload.responsible_id,
      status: payload.status
    })
    .eq('id', payload.id)

  if (articleError) {
    throw new Error(`Erro ao atualizar artigo: ${articleError.message}`)
  }

  // Buscar a última versão real da tabela de versões
  const { data: lastVersion, error: versionError } = await supabase
    .from('wiki_article_versions')
    .select('version_number')
    .eq('article_id', payload.id)
    .order('version_number', { ascending: false })
    .limit(1)
    .single()


  const currentVersion = lastVersion?.version_number || 0
  const newVersionNumber = currentVersion + 1
  

  // Criar nova versão
  await createArticleVersion(
    payload.id,
    newVersionNumber,
    payload.title,
    payload.body,
    'Atualização do artigo'
  )

  // Atualizar current_version
  await supabase
    .from('wiki_articles')
    .update({ current_version: newVersionNumber })
    .eq('id', payload.id)

  // Atualizar tags
  await upsertArticleTags(payload.id, payload.tags)
}

export async function deleteArticle(id: string): Promise<void> {
  // Remover dependências antes para evitar violação de FK
  const { error: versionsError } = await supabase
    .from('wiki_article_versions')
    .delete()
    .eq('article_id', id)

  if (versionsError) {
    throw new Error(`Erro ao excluir versões do artigo: ${versionsError.message}`)
  }

  const { error: tagsError } = await supabase
    .from('wiki_article_tags')
    .delete()
    .eq('article_id', id)

  if (tagsError) {
    throw new Error(`Erro ao excluir tags do artigo: ${tagsError.message}`)
  }

  const { error: favoritesError } = await supabase
    .from('wiki_favorites')
    .delete()
    .eq('article_id', id)

  if (favoritesError) {
    throw new Error(`Erro ao excluir favoritos do artigo: ${favoritesError.message}`)
  }

  const { error: viewsError } = await supabase
    .from('wiki_views')
    .delete()
    .eq('article_id', id)

  if (viewsError) {
    throw new Error(`Erro ao excluir visualizações do artigo: ${viewsError.message}`)
  }

  const { error } = await supabase
    .from('wiki_articles')
    .delete()
    .eq('id', id)

  if (error) {
    throw new Error(`Erro ao excluir artigo: ${error.message}`)
  }
}

// ===== FUNÇÕES AUXILIARES =====
async function upsertArticleTags(articleId: string, tagNames: string[]): Promise<void> {
  // Primeiro, remover todas as tags atuais
  await supabase
    .from('wiki_article_tags')
    .delete()
    .eq('article_id', articleId)

  if (tagNames.length === 0) return

  // Buscar ou criar tags
  const tagIds: string[] = []
  
  for (const tagName of tagNames) {
    // Verificar se tag já existe
    const { data: existingTag } = await supabase
      .from('wiki_tags')
      .select('id')
      .eq('name', tagName)
      .single()

    if (existingTag) {
      tagIds.push(existingTag.id)
    } else {
      // Criar nova tag
      const tagId = await createTag({ name: tagName })
      tagIds.push(tagId)
    }
  }

  // Associar tags ao artigo
  if (tagIds.length > 0) {
    const { error } = await supabase
      .from('wiki_article_tags')
      .insert(
        tagIds.map(tagId => ({
          article_id: articleId,
          tag_id: tagId
        }))
      )

    if (error) {
      throw new Error(`Erro ao associar tags: ${error.message}`)
    }
  }
}

// ===== BUSCA FULL-TEXT =====
export async function searchArticles(query: string): Promise<WikiArticle[]> {
  const { data, error } = await supabase
    .from('wiki_articles')
    .select(`
      *,
      category:wiki_categories(*),
      tags:wiki_article_tags(
        tag:wiki_tags(*)
      )
    `)
    .textSearch('search', query)
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`Erro na busca: ${error.message}`)
  }

  let articles = (data || []).map(article => ({
    ...article,
    tags: article.tags?.map((t: any) => t.tag).filter(Boolean) || []
  }))

  // Anexar departamentos via join no cliente
  const departmentIds = Array.from(new Set(
    (articles.map(a => a.department_id).filter(Boolean) as string[])
  ))

  if (departmentIds.length > 0) {
    const { data: departmentsData } = await supabase
      .from('wiki_departments')
      .select('*')
      .in('id', departmentIds)

    if (departmentsData) {
      const deptMap = new Map<string, any>(
        departmentsData.map((d: any) => [d.id, d])
      )
      articles = articles.map(a => ({
        ...a,
        department: a.department_id ? deptMap.get(a.department_id) : undefined
      }))
    }
  }

  return articles
}
